import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AppIcon } from '../ui/icon'
import {
    mdiCheck,
    mdiClose,
    mdiContentCopy,
    mdiDelete,
    mdiFolder,
    mdiLoading,
    mdiPencil,
} from '@mdi/js'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Badge } from '../ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../ui/alert-dialog'
import { SearchableSelect } from '../SearchableSelect'
import { useLocale } from '../../contexts/LocaleContext'
import { useCan } from '../../contexts/PermissionsContext'
import { documentTypeDisplayName, folderDisplayName } from './display-names'
import type { components } from '../../api/schema'
import { EmptyState } from '../ui/empty-state'

type FolderDefinition = components['schemas']['FolderDefinition']
type DocumentType = components['schemas']['DocumentType']

export const NONE_OPTION = '__none__'

// Re-exported so existing consumers keep a single import site for the editor
// and its display helpers.
export { documentTypeDisplayName, folderDisplayName }

/** Uids of `uid` and every folder below it — never valid parents (cycle guard). */
export function subtreeUids(folders: FolderDefinition[], uid: string): Set<string> {
    const result = new Set<string>([uid])
    let grew = true
    let guard = 0
    while (grew && guard++ < 100) {
        grew = false
        for (const folder of folders) {
            const parent = folder.parent_folder_uid
            if (parent && result.has(parent) && !result.has(folder.uid)) {
                result.add(folder.uid)
                grew = true
            }
        }
    }
    return result
}

export interface FolderUpdatePayload {
    /** Import/export key — renamable, never clearable. */
    code: string
    name: { [key: string]: string }
    parent_folder_uid: string | null
    document_type_uid: string | null
}

/**
 * Inline editing commits one field at a time, so every write is a PARTIAL
 * body. The PATCH endpoint is tri-state per field (omit = keep, null = clear,
 * value = set), which is exactly what this shape expresses — see
 * `UpdateFolderDefinitionRequest` in api/realassets-api.yaml.
 */
export type FolderUpdatePatch = Partial<FolderUpdatePayload>

/** The inline-editable fields, used as per-field saving/error keys. */
type EditableField = 'name_en' | 'name_fr' | 'parent' | 'document_type' | 'code'

interface FolderDefinitionEditorProps {
    folder: FolderDefinition | null
    /** Outline number computed by the tree (`1.2.1`), so both panes agree. */
    number?: string
    allFolders: FolderDefinition[]
    documentTypes: DocumentType[]
    onUpdate: (data: FolderUpdatePatch) => Promise<void>
    onDelete: () => Promise<void>
    onSelectFolder: (uid: string) => void
    isDeleting?: boolean
}

interface InlineTextFieldProps {
    label: string
    value: string
    /** When false the field renders its stored value as plain, non-editable
     *  text: no click-to-edit trigger, no hover affordance, no save/cancel. */
    canWrite: boolean
    placeholder: string
    testId: string
    isSaving: boolean
    error?: string
    editLabel: string
    saveLabel: string
    cancelLabel: string
    savingLabel: string
    onCommit: (next: string) => Promise<void>
}

/**
 * Click-to-edit text field, per the canonical pattern (§3 of
 * docs/frontend/patterns.md, rendered at `/$org/patterns#pattern-inline-edit-text`):
 * pencil on hover, Enter or the check button commits, Escape cancels, blur
 * commits. Each commit is its own PATCH — there is no page-level Save.
 */
function InlineTextField({
    label,
    value,
    canWrite,
    placeholder,
    testId,
    isSaving,
    error,
    editLabel,
    saveLabel,
    cancelLabel,
    savingLabel,
    onCommit,
}: InlineTextFieldProps) {
    const [isEditing, setIsEditing] = useState(false)
    const [draft, setDraft] = useState(value)
    // Closing the input unmounts it, which can still fire a blur in some
    // browsers — that blur would either re-commit what Enter just wrote
    // (double PATCH) or commit the very edit Escape just cancelled. This flag
    // makes the first of commit/cancel win and every later call a no-op.
    const settledRef = useRef(false)

    // A live refetch (or picking another folder) must not silently keep a
    // stale draft around for the next time this field is opened.
    useEffect(() => {
        if (!isEditing) setDraft(value)
    }, [value, isEditing])

    const commit = useCallback(async () => {
        if (settledRef.current) return
        settledRef.current = true
        setIsEditing(false)
        if (draft === value) return
        await onCommit(draft)
    }, [draft, value, onCommit])

    const cancel = useCallback(() => {
        settledRef.current = true
        setDraft(value)
        setIsEditing(false)
    }, [value])

    return (
        <div>
            <Label htmlFor={`${testId}-input`}>{label}</Label>
            {!canWrite ? (
                // Read-only: the value stays on screen, it just stops being a
                // button (no cursor-pointer, no hover border, no pencil).
                <div
                    className="flex min-h-9 w-full items-center gap-2 rounded border border-transparent px-2 py-1 text-left text-sm"
                    data-testid={`${testId}-value`}
                >
                    <span className={value ? 'truncate' : 'truncate text-muted-foreground'}>
                        {value || placeholder}
                    </span>
                </div>
            ) : isEditing ? (
                <div className="flex items-center gap-1">
                    <Input
                        id={`${testId}-input`}
                        autoFocus
                        value={draft}
                        onChange={e => setDraft(e.target.value)}
                        onBlur={() => void commit()}
                        onKeyDown={e => {
                            if (e.key === 'Enter') {
                                e.preventDefault()
                                void commit()
                            }
                            if (e.key === 'Escape') {
                                e.preventDefault()
                                cancel()
                            }
                        }}
                        data-testid={testId}
                    />
                    <Button
                        variant="ghost"
                        size="icon"
                        // onMouseDown, not onClick: the input's blur fires
                        // first on a plain click and would unmount this button
                        // before its click ever lands.
                        onMouseDown={e => {
                            e.preventDefault()
                            void commit()
                        }}
                        aria-label={saveLabel}
                        title={saveLabel}
                        data-testid={`${testId}-save`}
                    >
                        <AppIcon path={mdiCheck} size="sm" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        onMouseDown={e => {
                            e.preventDefault()
                            cancel()
                        }}
                        aria-label={cancelLabel}
                        title={cancelLabel}
                        data-testid={`${testId}-cancel`}
                    >
                        <AppIcon path={mdiClose} size="sm" />
                    </Button>
                </div>
            ) : (
                <button
                    type="button"
                    onClick={() => {
                        settledRef.current = false
                        setDraft(value)
                        setIsEditing(true)
                    }}
                    className="group flex min-h-9 w-full items-center gap-2 rounded border border-transparent px-2 py-1 text-left text-sm hover:border-input hover:bg-muted/50"
                    aria-label={editLabel}
                    title={editLabel}
                    data-testid={`${testId}-value`}
                >
                    <span className={value ? 'truncate' : 'truncate text-muted-foreground'}>
                        {value || placeholder}
                    </span>
                    {isSaving ? (
                        // Wrapped in a span: the MDI icon component does not
                        // forward unknown props (data-testid) to the <svg>.
                        <span
                            className="ml-auto shrink-0 text-muted-foreground"
                            title={savingLabel}
                            data-testid={`${testId}-saving`}
                        >
                            <AppIcon path={mdiLoading} size="xs" spin />
                        </span>
                    ) : (
                        <AppIcon tone="muted" path={mdiPencil} size="xs" className="ml-auto shrink-0 opacity-0 transition-opacity group-hover:opacity-100" />
                    )}
                </button>
            )}
            {error && (
                <p className="mt-1 text-xs text-destructive" role="status" data-testid={`${testId}-error`}>
                    {error}
                </p>
            )}
        </div>
    )
}

export function FolderDefinitionEditor({
    folder,
    number,
    allFolders,
    documentTypes,
    onUpdate,
    onDelete,
    onSelectFolder,
    isDeleting = false,
}: FolderDefinitionEditorProps) {
    const { t } = useTranslation('folders')
    // Multilingual `name` maps are resolved against the active UI locale, and
    // the locale is a dependency of every memo below so a language switch
    // re-labels this pane live.
    const { locale } = useLocale()
    // Folder layouts are configuration: without `configuration.write` this
    // pane is a viewer. Every write affordance below is hidden (actions) or
    // turned read-only (fields) — the backend is what enforces.
    const canWrite = useCan('configuration.write')
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)
    const [error, setError] = useState<string | null>(null)
    // Per-field saving/error state — there is no page-level form to fail, so
    // a failed commit annotates only the field that produced it.
    const [savingField, setSavingField] = useState<EditableField | null>(null)
    const [fieldErrors, setFieldErrors] = useState<Partial<Record<EditableField, string>>>({})
    // Non-blocking copy feedback (no toasts in this codebase): the icon flips
    // to a check and a one-line confirmation appears for a couple of seconds.
    const [uidCopied, setUidCopied] = useState(false)

    const folderUid = folder?.uid
    useEffect(() => {
        // Selecting another folder clears anything left over from the previous
        // one. Nothing else is reset on `folder` identity changes: a live
        // refetch must not wipe an in-flight error the user hasn't seen yet.
        setError(null)
        setFieldErrors({})
        setSavingField(null)
        setUidCopied(false)
    }, [folderUid])

    useEffect(() => {
        if (!uidCopied) return
        const timer = setTimeout(() => setUidCopied(false), 2000)
        return () => clearTimeout(timer)
    }, [uidCopied])

    const copyUid = useCallback(async () => {
        if (!folderUid) return
        try {
            await navigator.clipboard.writeText(folderUid)
            setUidCopied(true)
        } catch {
            // A denied clipboard permission is not worth an error banner: the
            // uid is on screen and selectable.
            setUidCopied(false)
        }
    }, [folderUid])

    const commitField = useCallback(
        async (field: EditableField, patch: FolderUpdatePatch) => {
            setSavingField(field)
            setFieldErrors(prev => {
                const next = { ...prev }
                delete next[field]
                return next
            })
            try {
                await onUpdate(patch)
            } catch (e) {
                setFieldErrors(prev => ({
                    ...prev,
                    [field]: t('folders.editor.update_error') + ' ' + (e instanceof Error ? e.message : ''),
                }))
            } finally {
                setSavingField(null)
            }
        },
        [onUpdate, t]
    )

    // The whole `name` map is always sent, not just the edited locale — a
    // PATCH with `{name: {en: …}}` would replace the jsonb column and drop the
    // French label.
    const commitName = useCallback(
        (locale: 'en' | 'fr', value: string) => {
            const merged = { ...(folder?.name || {}), [locale]: value }
            return commitField(locale === 'en' ? 'name_en' : 'name_fr', { name: merged })
        },
        [folder, commitField]
    )

    const parentOptions = useMemo(() => {
        if (!folder) return []
        const excluded = subtreeUids(allFolders, folder.uid)
        return [
            { value: NONE_OPTION, label: t('folders.no_parent') },
            ...allFolders
                .filter(f => !excluded.has(f.uid))
                .map(f => ({ value: f.uid, label: folderDisplayName(f, locale) }))
                .sort((a, b) => a.label.localeCompare(b.label)),
        ]
    }, [allFolders, folder, locale, t])

    // A document type may be claimed by at most one folder of a layout, so only
    // offer the types no *other* folder already took.
    const documentTypeOptions = useMemo(() => {
        const claimed = new Set(
            allFolders
                .filter(f => f.uid !== folder?.uid && f.document_type_uid)
                .map(f => f.document_type_uid as string)
        )
        return [
            { value: NONE_OPTION, label: t('folders.no_document_type') },
            ...documentTypes
                .filter(dt => !claimed.has(dt.uid))
                .map(dt => ({ value: dt.uid, label: documentTypeDisplayName(dt, locale) }))
                .sort((a, b) => a.label.localeCompare(b.label)),
        ]
    }, [allFolders, documentTypes, folder, locale, t])

    const children = useMemo(
        () =>
            allFolders
                .filter(f => f.parent_folder_uid === folder?.uid)
                // `uid` tie-break keeps this list identical to the tree's own
                // sort (see hierarchy/outline-numbering.ts).
                .sort((a, b) => (a.ordering || 0) - (b.ordering || 0) || a.uid.localeCompare(b.uid)),
        [allFolders, folder]
    )

    if (!folder) {
        return (
            <div
                className="flex h-full items-center justify-center p-8"
                data-testid="folder-editor-empty"
            >
                <EmptyState
                    variant="inline"
                    icon={mdiFolder}
                    title={t('folders.editor.none_selected_title')}
                    description={t('folders.editor.none_selected_description')}
                />
            </div>
        )
    }

    const documentTypeName = folder.document_type_uid
        ? documentTypes.find(dt => dt.uid === folder.document_type_uid)
        : undefined

    const handleDelete = async () => {
        setError(null)
        setConfirmDeleteOpen(false)
        try {
            await onDelete()
        } catch (e) {
            setError(t('folders.editor.delete_error') + ' ' + (e instanceof Error ? e.message : ''))
        }
    }

    return (
        <div className="flex h-full flex-col" data-testid="folder-editor">
            <div className="border-b p-4 md:p-6">
                <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <h2 className="flex items-baseline gap-2 text-xl font-bold md:text-2xl">
                            {number && (
                                <span
                                    className="shrink-0 font-mono text-base font-normal tabular-nums text-muted-foreground"
                                    title={t('folders.editor.outline_number_hint')}
                                    data-testid="folder-editor-number"
                                >
                                    {number}
                                </span>
                            )}
                            <span className="truncate" data-testid="folder-editor-title">
                                {folderDisplayName(folder, locale)}
                            </span>
                        </h2>
                        {documentTypeName && (
                            <Badge variant="secondary" className="mt-1 text-xs" data-testid="folder-editor-type-badge">
                                {documentTypeDisplayName(documentTypeName, locale)}
                            </Badge>
                        )}
                    </div>
                    {/* No Edit button: every field below is click-to-edit and
                        commits its own PATCH, so Delete is the only header
                        action left (icon-only, per front/CLAUDE.md). */}
                    <div className="flex shrink-0 items-center gap-2">
                        {canWrite && (
                            <Button
                                variant="destructive"
                                size="icon"
                                onClick={() => setConfirmDeleteOpen(true)}
                                disabled={isDeleting}
                                aria-label={t('folders.editor.delete')}
                                title={t('folders.editor.delete')}
                                data-testid="folder-editor-delete"
                            >
                                <AppIcon path={mdiDelete} size="sm" />
                            </Button>
                        )}
                    </div>
                </div>
            </div>

            <div className="flex-1 space-y-6 overflow-y-auto p-4 md:p-6">
                {error && (
                    <div
                        className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
                        role="status"
                        data-testid="folder-editor-error"
                    >
                        {error}
                    </div>
                )}

                <Card>
                    <CardHeader>
                        <CardTitle className="text-base">{t('folders.editor.basic_info')}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <InlineTextField
                                label={`🇺🇸 ${t('folders.editor.name_en_label')}`}
                                value={folder.name?.en || ''}
                                canWrite={canWrite}
                                placeholder={t('folders.editor.not_set')}
                                testId="folder-editor-name-en"
                                isSaving={savingField === 'name_en'}
                                error={fieldErrors.name_en}
                                editLabel={t('folders.editor.edit_field', {
                                    field: t('folders.editor.name_en_label'),
                                })}
                                saveLabel={t('folders.editor.save')}
                                cancelLabel={t('folders.editor.cancel')}
                                savingLabel={t('folders.editor.saving')}
                                onCommit={value => commitName('en', value)}
                            />
                            <InlineTextField
                                label={`🇫🇷 ${t('folders.editor.name_fr_label')}`}
                                value={folder.name?.fr || ''}
                                canWrite={canWrite}
                                placeholder={t('folders.editor.not_set')}
                                testId="folder-editor-name-fr"
                                isSaving={savingField === 'name_fr'}
                                error={fieldErrors.name_fr}
                                editLabel={t('folders.editor.edit_field', {
                                    field: t('folders.editor.name_fr_label'),
                                })}
                                saveLabel={t('folders.editor.save')}
                                cancelLabel={t('folders.editor.cancel')}
                                savingLabel={t('folders.editor.saving')}
                                onCommit={value => commitName('fr', value)}
                            />
                        </div>

                        {/* Selects need no edit mode: picking an option IS the
                            commit, so each one PATCHes just its own field. */}
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <div>
                                <div className="flex items-center gap-2">
                                    <Label>{t('folders.editor.parent_label')}</Label>
                                    {savingField === 'parent' && (
                                        <span
                                            className="text-muted-foreground"
                                            title={t('folders.editor.saving')}
                                            data-testid="folder-editor-parent-saving"
                                        >
                                            <AppIcon path={mdiLoading} size="xs" spin />
                                        </span>
                                    )}
                                </div>
                                <SearchableSelect
                                    options={parentOptions}
                                    value={folder.parent_folder_uid || NONE_OPTION}
                                    onValueChange={value => {
                                        const next = value === NONE_OPTION || value === '' ? null : value
                                        if (next === (folder.parent_folder_uid || null)) return
                                        void commitField('parent', { parent_folder_uid: next })
                                    }}
                                    disabled={!canWrite || savingField === 'parent'}
                                    placeholder={t('folders.select_parent_placeholder')}
                                    searchPlaceholder={t('folders.editor.search_placeholder')}
                                    emptyMessage={t('folders.editor.no_results')}
                                    data-testid="folder-editor-parent"
                                />
                                {fieldErrors.parent && (
                                    <p
                                        className="mt-1 text-xs text-destructive"
                                        role="status"
                                        data-testid="folder-editor-parent-error"
                                    >
                                        {fieldErrors.parent}
                                    </p>
                                )}
                            </div>
                            <div>
                                <div className="flex items-center gap-2">
                                    <Label>{t('folders.editor.document_type_label')}</Label>
                                    {savingField === 'document_type' && (
                                        <span
                                            className="text-muted-foreground"
                                            title={t('folders.editor.saving')}
                                            data-testid="folder-editor-document-type-saving"
                                        >
                                            <AppIcon path={mdiLoading} size="xs" spin />
                                        </span>
                                    )}
                                </div>
                                <SearchableSelect
                                    options={documentTypeOptions}
                                    value={folder.document_type_uid || NONE_OPTION}
                                    onValueChange={value => {
                                        const next = value === NONE_OPTION || value === '' ? null : value
                                        if (next === (folder.document_type_uid || null)) return
                                        void commitField('document_type', { document_type_uid: next })
                                    }}
                                    disabled={!canWrite || savingField === 'document_type'}
                                    placeholder={t('folders.select_document_type_placeholder')}
                                    searchPlaceholder={t('folders.editor.search_placeholder')}
                                    emptyMessage={t('folders.editor.no_results')}
                                    data-testid="folder-editor-document-type"
                                />
                                {fieldErrors.document_type && (
                                    <p
                                        className="mt-1 text-xs text-destructive"
                                        role="status"
                                        data-testid="folder-editor-document-type-error"
                                    >
                                        {fieldErrors.document_type}
                                    </p>
                                )}
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle className="text-base">
                            {t('folders.editor.children_title', { count: children.length })}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {children.length === 0 ? (
                            <EmptyState
                                variant="inline"
                                title={t('folders.editor.no_children')}
                                data-testid="folder-editor-no-children"
                            />
                        ) : (
                            <div className="space-y-1">
                                {children.map(child => (
                                    <button
                                        key={child.uid}
                                        type="button"
                                        onClick={() => onSelectFolder(child.uid)}
                                        className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-muted/50"
                                        data-testid={`folder-editor-child-${child.uid}`}
                                    >
                                        <AppIcon tone="muted" path={mdiFolder} size="sm" className="shrink-0" />
                                        <span className="truncate">{folderDisplayName(child, locale)}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Technical: the identifiers a customer needs when the layout
                    travels through a CSV/JSON round-trip. */}
                <Card>
                    <CardHeader>
                        <CardTitle className="text-base">{t('folders.editor.technical_title')}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div>
                            <InlineTextField
                                label={t('folders.editor.code_label')}
                                value={folder.code || ''}
                                canWrite={canWrite}
                                placeholder={t('folders.editor.not_set')}
                                testId="folder-editor-code"
                                isSaving={savingField === 'code'}
                                error={fieldErrors.code}
                                editLabel={t('folders.editor.edit_field', {
                                    field: t('folders.editor.code_label'),
                                })}
                                saveLabel={t('folders.editor.save')}
                                cancelLabel={t('folders.editor.cancel')}
                                savingLabel={t('folders.editor.saving')}
                                onCommit={value => commitField('code', { code: value })}
                            />
                            {/* No blocking warning on rename (spec 2026-08-02-02
                                open question): one hint line is enough. */}
                            <p className="mt-1 text-xs text-muted-foreground" data-testid="folder-editor-code-hint">
                                {t('folders.editor.code_hint')}
                            </p>
                        </div>

                        <div>
                            <Label>{t('folders.editor.uid_label')}</Label>
                            <div className="flex items-center gap-1">
                                <span
                                    className="min-w-0 flex-1 truncate rounded border border-input bg-muted/30 px-2 py-1.5 font-mono text-xs"
                                    data-testid="folder-editor-uid"
                                >
                                    {folder.uid}
                                </span>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => void copyUid()}
                                    aria-label={t('folders.editor.copy_uid')}
                                    title={t('folders.editor.copy_uid')}
                                    data-testid="folder-editor-uid-copy"
                                >
                                    <AppIcon path={uidCopied ? mdiCheck : mdiContentCopy} size="sm" />
                                </Button>
                            </div>
                            {uidCopied && (
                                <p
                                    className="mt-1 text-xs text-muted-foreground"
                                    role="status"
                                    data-testid="folder-editor-uid-copied"
                                >
                                    {t('folders.editor.uid_copied')}
                                </p>
                            )}
                        </div>
                    </CardContent>
                </Card>
            </div>

            <AlertDialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
                <AlertDialogContent data-testid="folder-delete-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('folders.editor.delete_dialog.title')}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t('folders.editor.delete_dialog.description', {
                                name: folderDisplayName(folder, locale),
                            })}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="folder-delete-cancel">
                            {t('folders.editor.delete_dialog.cancel')}
                        </AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} data-testid="folder-delete-confirm">
                            {t('folders.editor.delete_dialog.confirm')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    )
}
